import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_GRADIENT,
  type GradientConfig,
} from "@/lib/tier";
import type { OverlayConfig } from "@/components/image-studio/excerpt-image/OverlayPanel";

export type GradientPresetRow = {
  id: string;
  name: string;
  gradient: GradientConfig;
  workspace_id: string;
};

export type BrandColorRow = {
  id?: string;
  slot_index: number;
  label: string | null;
  hex: string;
};

type ActiveWorkspace = {
  id: string;
  owner_user_id: string;
};

export function useExcerptImageStudioState(active: ActiveWorkspace | null | undefined) {

  const [bgMode, setBgMode] = useState<"light" | "dark" | "custom" | "brand" | "gradient">("light");
  const [bgColor, setBgColor] = useState("#FBFAF7");
  const [fontStyle, setFontStyle] = useState<"serif" | "sans">("serif");
  const [fontFamily, setFontFamily] = useState("Fraunces");
  const [bodyHtml, setBodyHtml] = useState("<p>Type or paste your excerpt here.</p>");
  const [authorName, setAuthorName] = useState("");
  const [pubTitle, setPubTitle] = useState("");
  const [displayUrl, setDisplayUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [brandColors, setBrandColors] = useState<BrandColorRow[]>([]);
  const [gradient, setGradient] = useState<GradientConfig>(DEFAULT_GRADIENT);
  const [gradientPresets, setGradientPresets] = useState<GradientPresetRow[]>([]);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [deletingPresetId, setDeletingPresetId] = useState<string | null>(null);
  const [overlay, setOverlay] = useState<OverlayConfig>({ enabled: false, color: "#000000", opacity: 0.3, blur: 0 });
  const [draftHydrated, setDraftHydrated] = useState(false);

  useEffect(() => {
    if (!active) return;
    setDraftHydrated(false);
    (async () => {
      const [settingsResult, brandResult, presetsResult] = await Promise.all([
        supabase.from("excerpt_image_generator_settings").select("*").eq("workspace_id", active.id).maybeSingle(),
        supabase.from("brand_colors").select("*").eq("workspace_id", active.id).order("slot_index"),
        supabase.from("excerpt_gradient_presets").select("id,name,gradient,workspace_id").eq("workspace_id", active.id).order("updated_at", { ascending: false }),
      ]);

      const data = settingsResult.data as (typeof settingsResult.data & {
        bg_mode?: string;
        font_style?: string;
        bg_gradient?: GradientConfig | null;
        overlay_enabled?: boolean | null;
        overlay_color?: string | null;
        overlay_opacity?: number | null;
        overlay_blur?: number | null;
      }) | null;
      if (data) {
        setBgMode((data.bg_mode as "light" | "dark" | "gradient" | undefined) ?? "light");
        setBgColor(data.bg_color);
        setFontStyle((data.font_style as "serif" | "sans" | undefined) ?? "serif");
        setFontFamily(data.font_family);
        setDisplayUrl(data.display_url ?? "");
        setLogoPath(data.logo_path);
        const g = data.bg_gradient;
        if (g && Array.isArray(g.stops) && g.stops.length >= 2) setGradient(g as GradientConfig);
        setOverlay({
          enabled: !!data.overlay_enabled,
          color: data.overlay_color ?? "#000000",
          opacity: Number(data.overlay_opacity ?? 0.3),
          blur: Number(data.overlay_blur ?? 0),
        });
        if (data.logo_path) {
          const { data: signed } = await supabase.storage.from("logos").createSignedUrl(data.logo_path, 3600);
          setLogoUrl(signed?.signedUrl ?? null);
        } else {
          setLogoUrl(null);
        }
      }
      setBrandColors(brandResult.data ?? []);
      setGradientPresets(((presetsResult.data as GradientPresetRow[] | null) ?? []).filter((preset) => preset?.gradient && Array.isArray((preset.gradient as GradientConfig).stops)));

      const prefill = sessionStorage.getItem("excerptus.imageStudioPrefill");
      if (prefill) {
        try {
          const p = JSON.parse(prefill);
          if (p.bodyHtml) setBodyHtml(p.bodyHtml);
          if (p.author) setAuthorName(p.author);
          if (p.publication) setPubTitle(p.publication);
        } catch {
          // Ignore malformed prefill JSON.
        }
        sessionStorage.removeItem("excerptus.imageStudioPrefill");
      } else {
        const draft = localStorage.getItem(`excerptus.imageStudioDraft.${active.id}`);
        if (draft) {
          try {
            const d = JSON.parse(draft);
            if (typeof d.bodyHtml === "string") setBodyHtml(d.bodyHtml);
            if (typeof d.author === "string") setAuthorName(d.author);
            if (typeof d.publication === "string") setPubTitle(d.publication);
          } catch {
            // Ignore malformed draft JSON.
          }
        }
      }
      setDraftHydrated(true);
    })();
  }, [active]);

  useEffect(() => {
    if (!active || !draftHydrated) return;
    localStorage.setItem(
      `excerptus.imageStudioDraft.${active.id}`,
      JSON.stringify({ bodyHtml, author: authorName, publication: pubTitle })
    );
  }, [active, draftHydrated, bodyHtml, authorName, pubTitle]);

  const persistSettings = (textColor: string) => {
    if (!active) return;
    return supabase.from("excerpt_image_generator_settings").upsert({
      workspace_id: active.id,
      bg_mode: bgMode,
      bg_color: bgColor,
      text_color: textColor,
      font_family: fontFamily,
      font_style: fontStyle,
      display_url: displayUrl || null,
      logo_path: logoPath,
      bg_gradient: gradient as unknown as import("@/integrations/supabase/types").Json,
      overlay_enabled: overlay.enabled,
      overlay_color: overlay.color,
      overlay_opacity: overlay.opacity,
      overlay_blur: overlay.blur,
    });
  };

  const onLogoUpload = async (file: File) => {
    if (!active) return;
    const ext = file.name.split(".").pop();
    const path = `${active.owner_user_id}/${active.id}/logo.${ext}`;
    const { error } = await supabase.storage.from("logos").upload(path, file, { upsert: true, contentType: file.type });
    if (error) return toast.error(error.message);
    setLogoPath(path);
    const { data } = await supabase.storage.from("logos").createSignedUrl(path, 3600);
    setLogoUrl(data?.signedUrl ?? null);
    toast.success("Logo uploaded");
  };

  const onLogoClear = async () => {
    if (!active) return;
    setLogoPath(null);
    setLogoUrl(null);
    if (logoPath) {
      await supabase.storage.from("logos").remove([logoPath]);
    }
    toast.success("Logo cleared — showing default Excerptus mark");
  };

  const saveGradientPreset = async () => {
    if (!active) return;
    const trimmedName = presetName.trim();
    if (!trimmedName) {
      toast.error("Enter a preset name first");
      return;
    }
    setSavingPreset(true);

    const existing = gradientPresets.find((preset) => preset.name.toLowerCase() === trimmedName.toLowerCase());
    const payload = {
      workspace_id: active.id,
      name: trimmedName,
      gradient: gradient as unknown as import("@/integrations/supabase/types").Json,
    };
    const result = existing
      ? await supabase.from("excerpt_gradient_presets").update(payload).eq("id", existing.id).select("id,name,gradient,workspace_id").single()
      : await supabase.from("excerpt_gradient_presets").insert(payload).select("id,name,gradient,workspace_id").single();
    setSavingPreset(false);
    if (result.error) {
      toast.error(result.error.message);
      return;
    }
    const savedPreset = result.data as GradientPresetRow;
    setGradientPresets((current) => {
      const remaining = current.filter((preset) => preset.id !== savedPreset.id && preset.name.toLowerCase() !== savedPreset.name.toLowerCase());
      return [savedPreset, ...remaining];
    });
    setPresetName("");
    toast.success(existing ? "Gradient preset updated" : "Gradient preset saved");
  };

  const applyGradientPreset = (presetId: string) => {
    const preset = gradientPresets.find((entry) => entry.id === presetId);
    if (!preset) return;
    setGradient(preset.gradient);
    toast.success(`Applied ${preset.name}`);
  };

  const deleteGradientPreset = async (presetId: string) => {
    if (!active) return;
    const preset = gradientPresets.find((entry) => entry.id === presetId);
    if (!preset) return;

    setDeletingPresetId(presetId);
    const { error } = await supabase.from("excerpt_gradient_presets").delete().eq("id", presetId);
    setDeletingPresetId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setGradientPresets((current) => current.filter((entry) => entry.id !== presetId));
    toast.success(`Deleted ${preset.name}`);
  };

  const saveBrandSlot = async (slot_index: number, hex: string, label: string) => {
    if (!active) return;
    const existing = brandColors.find((b) => b.slot_index === slot_index);
    if (existing?.id) {
      await supabase.from("brand_colors").update({ hex, label }).eq("id", existing.id);
    } else {
      await supabase.from("brand_colors").insert({ workspace_id: active.id, slot_index, hex, label });
    }
    const { data } = await supabase.from("brand_colors").select("*").eq("workspace_id", active.id).order("slot_index");
    setBrandColors(data ?? []);
  };

  const deleteBrandSlot = async (slot_index: number) => {
    if (!active) return;
    await supabase.from("brand_colors").delete().eq("workspace_id", active.id).eq("slot_index", slot_index);
    setBrandColors(brandColors.filter((b) => b.slot_index !== slot_index));
  };

  return {
    bgMode, setBgMode,
    bgColor, setBgColor,
    fontStyle, setFontStyle,
    fontFamily, setFontFamily,
    bodyHtml, setBodyHtml,
    authorName, setAuthorName,
    pubTitle, setPubTitle,
    displayUrl, setDisplayUrl,
    logoUrl, logoPath,
    brandColors,
    gradient, setGradient,
    gradientPresets,
    presetName, setPresetName,
    savingPreset, deletingPresetId,
    overlay, setOverlay,
    persistSettings,
    onLogoUpload, onLogoClear,
    saveGradientPreset, applyGradientPreset, deleteGradientPreset,
    saveBrandSlot, deleteBrandSlot,
  };
}
